import React, { useState, useEffect } from 'react';
import { Label, TextInput, Select } from 'flowbite-react';
import { Clock, Trash2, GripVertical } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { gamesApi } from '../../api/games';
import toast from 'react-hot-toast';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const THUMBNAILS = [
  'Swertres',
  'STL',
  '2D LOTTO',
  '3D LOTTO',
  '4D LOTTO',
  '6D LOTTO',
];

export default function EditGameSettings({ isOpen, onClose, gameData }) {
  const { tokens } = useAuth();
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Form states
  const [name, setName] = useState(gameData?.name || '');
  const [gameTicketLabel, setGameTicketLabel] = useState(
    gameData?.otherSettings?.gameTicketLabel || ''
  );
  const [thumbnail, setThumbnail] = useState(
    gameData?.otherSettings?.image || ''
  );
  const [timeSlots, setTimeSlots] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      setTimeout(() => setMounted(true), 10);
    } else {
      setMounted(false);
      setTimeout(() => setVisible(false), 300);
    }
  }, [isOpen]);

  useEffect(() => {
    if (gameData) {
      setName(gameData.name || '');
      setThumbnail(gameData.otherSettings?.image || '');
      setGameTicketLabel(gameData.otherSettings?.gameTicketLabel || '');

      const drawTimes = gameData.otherSettings?.drawTime || [];
      const cutOffTimes = gameData.otherSettings?.cutOffTime || [];

      const slots = drawTimes.map((drawTime, index) => ({
        drawTime: drawTime.split(':').slice(0, 2).join(':'), // Trim seconds
        cutOffTime: (cutOffTimes[index] || '').split(':').slice(0, 2).join(':'),
      }));

      setTimeSlots(
        slots.length > 0 ? slots : [{ drawTime: '', cutOffTime: '' }]
      );
    }
  }, [gameData]);

  if (!visible) return null;

  const handleAddTimeSlot = () => {
    setTimeSlots([...timeSlots, { drawTime: '', cutOffTime: '' }]);
  };

  const handleRemoveTimeSlot = (index) => {
    setTimeSlots(timeSlots.filter((_, i) => i !== index));
  };

  // Function to handle drag end events
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(timeSlots);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setTimeSlots(items);
  };

  // Function to sort time slots by draw time
  const sortTimeSlots = (slots) => {
    return [...slots].sort((a, b) => {
      if (!a.drawTime) return 1;
      if (!b.drawTime) return -1;
      return a.drawTime.localeCompare(b.drawTime);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Sort time slots by draw time before saving
      const sortedTimeSlots = sortTimeSlots(timeSlots);

      const response = await gamesApi.updateGameSettings(
        tokens.access.token,
        gameData.id,
        {
          name,
          otherSettings: {
            image: thumbnail,
            gameTicketLabel,
            drawTime: sortedTimeSlots.map((slot) => slot.drawTime),
            cutOffTime: sortedTimeSlots.map((slot) => slot.cutOffTime),
          },
        }
      );

      if (response.status === 200) {
        toast.success('Game settings updated successfully');
        onClose();
      } else {
        toast.error('Failed to update game settings');
      }
    } catch (error) {
      console.error('Error updating game settings:', error);
      toast.error('Error updating game settings');
    }
  };

  return (
    <div
      className='fixed inset-0 bg-black/50 flex items-center justify-center px-4 z-[60]'
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`w-full max-w-md bg-white rounded-lg transform transition-transform duration-300 ${
          mounted ? 'translate-y-0' : 'translate-y-full'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit} className='p-4'>
          <div className='mb-4'>
            <Label htmlFor='name'>Name</Label>
            <TextInput
              id='name'
              value={name}
              onChange={(e) => setName(e.target.value)}
              className='w-full'
            />
          </div>

          <div className='mb-4'>
            <Label htmlFor='name'>Game Ticket Label</Label>
            <TextInput
              id='name'
              value={gameTicketLabel}
              onChange={(e) => setGameTicketLabel(e.target.value)}
              className='w-full'
            />
          </div>

          <div className='mb-4'>
            <Label htmlFor='thumbnail'>Thumbnail</Label>
            <Select
              id='thumbnail'
              value={thumbnail}
              onChange={(e) => setThumbnail(e.target.value)}
              className='w-full'
            >
              <option value='PCSOSLG'>Swertres</option>
              <option value='PCSOSTL'>STL</option>
              <option value='PSCOSTLMISOR'>STL (MISOR)</option>
              <option value='PSCOSTLCDO'>STL (CDO)</option>
              <option value='PSCOSTLBUKIDNON'>STL (BUKIDNON)</option>
              <option value='PCSOEZ2LG'>2D</option>
              <option value='PCSOPICK3LG'>Pick 3</option>
              <option value='PCSOLAST2'>Last 2</option>
              <option value='PCSOLAST3'>Last 3</option>
              <option value='PSCOS2'>S2</option>
              <option value='PSCO4D'>4D</option>
              <option value='PSCO6D'>6D</option>
            </Select>
          </div>

          <div className='mb-4'>
            <div className='flex justify-between mb-2'>
              <div className='w-1/2 pr-2'>
                <Label>Draw Time</Label>
              </div>
              <div className='w-1/2 pl-2'>
                <Label>Cut off time (24hrs)</Label>
              </div>
            </div>

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId='timeSlots'>
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {timeSlots.map((slot, index) => (
                      <Draggable
                        key={index}
                        draggableId={`time-slot-${index}`}
                        index={index}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className='flex items-center gap-2 mb-2'
                          >
                            <div
                              {...provided.dragHandleProps}
                              className='cursor-grab'
                            >
                              <GripVertical className='w-4 h-4 text-gray-500' />
                            </div>
                            <div className='w-1/2 pr-2'>
                              <div className='relative'>
                                <TextInput
                                  type='time'
                                  value={slot.drawTime}
                                  onChange={(e) => {
                                    const newSlots = [...timeSlots];
                                    newSlots[index].drawTime = e.target.value;
                                    setTimeSlots(newSlots);
                                  }}
                                  className='w-full'
                                />
                                <Clock className='absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500' />
                              </div>
                            </div>
                            <div className='w-1/2 pl-2'>
                              <div className='relative'>
                                <TextInput
                                  type='time'
                                  value={slot.cutOffTime}
                                  onChange={(e) => {
                                    const newSlots = [...timeSlots];
                                    newSlots[index].cutOffTime = e.target.value;
                                    setTimeSlots(newSlots);
                                  }}
                                  className='w-full'
                                />
                                <Clock className='absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500' />
                              </div>
                            </div>
                            <button
                              type='button'
                              onClick={() => handleRemoveTimeSlot(index)}
                              className='text-red-500'
                            >
                              <Trash2 className='w-4 h-4' />
                            </button>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            <button
              type='button'
              onClick={handleAddTimeSlot}
              className='text-[#0E64D1] text-sm font-medium mt-2'
            >
              + ADD ANOTHER DRAW TIME
            </button>
          </div>

          <div className='flex gap-2 justify-end mt-6'>
            <button
              type='button'
              onClick={onClose}
              className='px-4 py-2 text-gray-700 border border-gray-300 rounded-md'
            >
              Cancel
            </button>
            <button
              type='submit'
              className='px-4 py-2 bg-[#0E64D1] text-white rounded-md'
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
